/*
 * RES - Brinde Automático
 *
 * Escuta o evento `cart-update` (pubsub.js) e mantém os brindes sincronizados:
 * - produto X no carrinho      -> adiciona o produto Y marcado com a propriedade de controle
 * - produto X removido         -> remove o brinde correspondente
 * - total atinge o valor mínimo -> adiciona o brinde da regra por valor (e remove abaixo dele)
 *
 * O brinde é identificado pela line item property `_brinde`, cujo valor é o id do
 * bloco da regra. Como a propriedade começa com "_", ela não aparece para o
 * cliente no carrinho nem no checkout.
 */
const GIFT_PROPERTY = '_brinde';

/*
 * Total usado pelas regras por valor: o do carrinho sem as linhas de brinde, para
 * que um brinde com preço não se sustente sozinho acima do mínimo. O snippet
 * shipping-bar faz a mesma conta em Liquid para a barra de progresso.
 */
function cartTotalWithoutGifts(cart) {
  return cart.items.reduce((total, item) => {
    if (item.properties && item.properties[GIFT_PROPERTY]) return total - item.final_line_price;
    return total;
  }, cart.total_price);
}

class GiftWithPurchase extends HTMLElement {
  constructor() {
    super();

    this.source = 'gift-with-purchase';
    this.enabled = this.dataset.enabled === 'true';
    this.rules = this.parseRules();
    this.busy = false;
    this.pending = false;
  }

  parseRules() {
    const script = this.querySelector('[data-gift-rules]');
    if (!script) return [];

    try {
      const rules = JSON.parse(script.textContent);
      // Uma regra em que o brinde é o próprio gatilho criaria um laço infinito.
      return rules.filter((rule) => rule.type === 'threshold' || rule.giftProductId !== rule.triggerProductId);
    } catch (error) {
      console.error('[brinde-automatico] regras inválidas', error);
      return [];
    }
  }

  connectedCallback() {
    if (!this.enabled || this.rules.length === 0) return;

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === this.source) return;
      return this.sync();
    });

    // Estado inicial: o carrinho pode ter sobrado de uma sessão anterior.
    this.sync();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  async sync() {
    if (this.busy) {
      // Uma nova atualização chegou no meio da sincronização: refaz ao terminar.
      this.pending = true;
      return;
    }

    this.busy = true;

    try {
      const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
      const changed = await this.applyRules(cart);

      if (changed) await this.refreshCart();
    } catch (error) {
      console.error('[brinde-automatico]', error);
    } finally {
      this.busy = false;

      if (this.pending) {
        this.pending = false;
        await this.sync();
      }
    }
  }

  /* Aplica todas as regras em sequência. Retorna true se o carrinho mudou. */
  async applyRules(cart) {
    let changed = false;

    for (const rule of this.rules) {
      const giftLine = cart.items.find((item) => this.giftIdOf(item) === rule.id);
      const wantedQuantity =
        rule.type === 'threshold'
          ? this.thresholdGiftQuantityFor(rule, cart)
          : this.giftQuantityFor(rule, this.triggerQuantityOf(rule, cart));

      if (wantedQuantity > 0 && !giftLine) {
        changed = (await this.addGift(rule, wantedQuantity)) || changed;
      } else if (giftLine && giftLine.quantity !== wantedQuantity) {
        changed = (await this.changeGift(giftLine.key, wantedQuantity)) || changed;
      }
    }

    return changed;
  }

  /*
   * Quantidade de brinde que a regra pede para a quantidade atual do gatilho.
   * Cumulativo: multiplica a cada múltiplo da quantidade mínima.
   * Não cumulativo: sempre a mesma quantidade, uma vez atingido o mínimo.
   */
  giftQuantityFor(rule, triggerQuantity) {
    if (triggerQuantity < rule.minQuantity) return 0;
    if (!rule.cumulative) return rule.giftQuantity;

    return Math.floor(triggerQuantity / rule.minQuantity) * rule.giftQuantity;
  }

  triggerQuantityOf(rule, cart) {
    return cart.items.reduce((total, item) => {
      if (item.product_id !== rule.triggerProductId) return total;
      if (this.giftIdOf(item)) return total; // um brinde nunca dispara outra regra
      return total + item.quantity;
    }, 0);
  }

  /* Regra por valor: entrega o brinde enquanto o total (sem brindes) estiver no mínimo. */
  thresholdGiftQuantityFor(rule, cart) {
    return cartTotalWithoutGifts(cart) >= rule.minAmount ? rule.giftQuantity : 0;
  }

  giftIdOf(item) {
    return (item.properties && item.properties[GIFT_PROPERTY]) || null;
  }

  async addGift(rule, quantity) {
    const response = await fetch(`${routes.cart_add_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [
          {
            id: rule.giftVariantId,
            quantity,
            properties: { [GIFT_PROPERTY]: rule.id },
          },
        ],
      }),
    });

    if (!response.ok) {
      // Brinde esgotado ou indisponível: segue sem quebrar o fluxo do carrinho.
      console.warn(`[brinde-automatico] não foi possível adicionar o brinde "${rule.giftTitle}"`);
      return false;
    }

    return true;
  }

  async changeGift(key, quantity) {
    const response = await fetch(`${routes.cart_change_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ id: key, quantity }),
    });

    return response.ok;
  }

  /*
   * O redesenho normal continua sendo o do tema: publicamos `cart-update` e os
   * subscribers existentes (cart-items / cart-drawer-items) se atualizam sozinhos.
   * Não mexemos no DOM do carrinho fora disso — só o ícone do header e o total da
   * página do carrinho ficam de fora desse caminho e são atualizados pontualmente.
   */
  async refreshCart() {
    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());

    // publish resolve só depois dos subscribers, então o que vier abaixo roda por último.
    await publish(PUB_SUB_EVENTS.cartUpdate, { source: this.source, cartData: cart });

    // Único caso que o caminho do tema não cobre: o carrinho esvaziou junto com o
    // brinde. O tema já tinha desenhado a tela com o carrinho ainda não-vazio (o
    // brinde estava lá) e o estado vazio fica fora dos trechos que ele troca.
    if (cart.item_count === 0) await this.renderEmptyCart();

    await this.refreshSection('cart-icon-bubble', '#cart-icon-bubble', '.shopify-section');
    await this.refreshSection('main-cart-footer', '#main-cart-footer .js-contents', '.js-contents');
  }

  async renderEmptyCart() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.classList.add('is-empty');

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.add('is-empty');

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) await this.renderEmptyDrawer(cartDrawer);
  }

  /* Recarrega o drawer inteiro a partir do HTML da section. */
  async renderEmptyDrawer(cartDrawer) {
    const target = cartDrawer.querySelector('#CartDrawer');
    if (!target) return;

    const responseText = await fetch(`${routes.cart_url}?section_id=cart-drawer`).then((response) => response.text());
    const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector('#CartDrawer');
    if (!source) return;

    target.innerHTML = source.innerHTML;
    cartDrawer.classList.add('is-empty');

    // O overlay foi recriado junto com o conteúdo: religa o clique de fechar.
    const overlay = target.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
  }

  async refreshSection(sectionId, targetSelector, sourceSelector) {
    const target = document.querySelector(targetSelector);
    if (!target) return;

    const responseText = await fetch(`${routes.cart_url}?section_id=${sectionId}`).then((response) => response.text());
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const source = html.querySelector(sourceSelector);

    if (source) target.innerHTML = source.innerHTML;
  }
}

customElements.define('gift-with-purchase', GiftWithPurchase);

/*
 * Barra "Faltam X para ganhar um brinde" do carrinho.
 *
 * O snippet shipping-bar renderiza o elemento vazio com o total do carrinho (sem
 * brindes) em data-cart-total; os textos e o valor mínimo vêm das regras por valor
 * da section. Como o carrinho redesenha o snippet a cada atualização, basta
 * desenhar no connectedCallback. Com mais de uma regra por valor, mostra a próxima
 * meta ainda não atingida; com todas atingidas, a mensagem da maior delas.
 */
class GiftProgressBar extends HTMLElement {
  connectedCallback() {
    const giftWithPurchase = document.querySelector('gift-with-purchase');
    const rules =
      giftWithPurchase && giftWithPurchase.enabled
        ? giftWithPurchase.rules
            .filter((rule) => rule.type === 'threshold' && rule.giftAvailable)
            .sort((a, b) => a.minAmount - b.minAmount)
        : [];

    if (rules.length === 0) return;

    const total = Number(this.dataset.cartTotal) || 0;
    const nextRule = rules.find((rule) => total < rule.minAmount);
    const rule = nextRule || rules[rules.length - 1];
    const progress = nextRule ? Math.min(100, Math.floor((total / rule.minAmount) * 100)) : 100;

    this.innerHTML = `
      <div class="shipping-bar__text">
        <span></span>
        <span class="svg-wrapper">${GiftProgressBar.icon}</span>
      </div>
      <div class="shipping-bar">
        <div class="shipping-bar__fill" style="--width: ${progress}%;"></div>
      </div>
    `;

    const text = this.querySelector('.shipping-bar__text span');
    if (nextRule) {
      this.appendTextWithAmount(text, rule.textMissing || 'Faltam [valor] para ganhar um brinde.', rule.minAmount - total);
    } else {
      const strong = document.createElement('b');
      strong.textContent = rule.textUnlocked || 'Você ganhou o brinde!';
      text.append(strong);
    }

    this.classList.toggle('is-complete', !nextRule);
    this.hidden = false;
  }

  /* Troca [valor] pelo valor formatado em negrito, sem interpretar o texto da regra como HTML. */
  appendTextWithAmount(target, template, cents) {
    const parts = template.split('[valor]');

    parts.forEach((part, index) => {
      target.append(part);
      if (index < parts.length - 1) {
        const strong = document.createElement('b');
        strong.textContent = GiftProgressBar.formatMoney(cents);
        target.append(strong);
      }
    });
  }

  static formatMoney(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'BRL';
    return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency }).format(
      cents / 100
    );
  }
}

GiftProgressBar.icon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';

customElements.define('gift-progress-bar', GiftProgressBar);
